<template>
  <div
    v-if="proxyMode || globalProxyMode"
    class="bk bk-field-list-proxy"
    :class="[
  {
    'bk-is-visible': proxyVisible
  },
  'bk-is-' + (dropAlignment || (nestingLevel === 0 ? 'vertical' : 'horizontal'))
]"
  >
    <div
      ref="root"
      class="bk-field-list-proxy-list bk-draggable-list-container"
      v-bind="fieldAttributes"
    >
      <BlokkliItem
        v-for="(item, i) in list"
        :key="item.uuid + fieldListType + definitions.renderKey.value"
        :uuid="item.uuid"
        :bundle="item.bundle"
        :is-new="item.isNew"
        :options="item.options"
        :props="item.props"
        is-editing
        :index="i"
        :parent-type="isNested ? entity.bundle : ''"
        data-editing="true"
        data-element-type="existing"
        :data-sortli-id="item.uuid"
        :data-uuid="item.uuid"
        :data-host-type="entity.type"
        :data-host-bundle="entity.bundle"
        :data-host-uuid="entity.uuid"
        :data-item-bundle="item.bundle"
        :data-host-field-name="name"
        :data-host-field-list-type="fieldListType"
        :data-is-nested="isNested"
        :data-is-new="item.isNew"
        :data-entity-type="runtimeConfig.itemEntityType"
        :data-bk-is-muted="isMuted(item)"
      />
    </div>
  </div>
  <Component
    :is="tag"
    v-else
    ref="root"
    :class="['bk-draggable-list-container', attrs.class]"
    v-bind="fieldAttributes"
  >
    <BlokkliItem
      v-for="(item, i) in list"
      :key="item.uuid + fieldListType"
      :uuid="item.uuid"
      :bundle="item.bundle"
      :is-new="item.isNew"
      :options="item.options"
      :props="item.props"
      is-editing
      :index="i"
      :parent-type="isNested ? entity.bundle : ''"
      data-editing="true"
      data-element-type="existing"
      :data-sortli-id="item.uuid"
      :data-uuid="item.uuid"
      :data-host-type="entity.type"
      :data-host-bundle="entity.bundle"
      :data-host-uuid="entity.uuid"
      :data-item-bundle="item.bundle"
      :data-host-field-name="name"
      :data-host-field-list-type="fieldListType"
      :data-is-nested="isNested"
      :data-is-new="item.isNew"
      :data-entity-type="runtimeConfig.itemEntityType"
      :data-bk-is-muted="isMuted(item)"
    />
  </Component>
</template>

<script setup>
import {
  computed,
  useBlokkli,
  ref,
  onMounted,
  onBeforeUnmount,
  useAttrs,
  provide,
  watch
} from "#imports";
import BlokkliItem from "./../BlokkliItem.vue";
import { isVisibleByOptions } from "#blokkli/helpers/runtimeHelpers";
import {
  INJECT_FIELD_PROXY_MODE,
  INJECT_IS_EDITING
} from "#blokkli/helpers/symbols";
const { dom, types, runtimeConfig, selection, definitions } = useBlokkli();
const root = ref(null);
const props = defineProps({
  name: { type: String, required: true },
  fieldKey: { type: String, required: true },
  list: { type: Array, required: true },
  entity: { type: Object, required: true },
  language: { type: String, required: false, default: void 0 },
  tag: { type: String, required: false, default: "div" },
  isNested: { type: Boolean, required: true },
  fieldListType: { type: String, required: true },
  allowedFragments: { type: Array, required: false, default: void 0 },
  dropAlignment: { type: String, required: false, default: void 0 },
  proxyMode: { type: Boolean, required: false },
  globalProxyMode: { type: Boolean, required: false },
  nestingLevel: { type: Number, required: true },
  shouldRenderItem: { type: Function, required: false, default: void 0 }
});
const attrs = useAttrs();
provide(INJECT_FIELD_PROXY_MODE, props.proxyMode);
provide(INJECT_IS_EDITING, true);
const proxyVisible = computed(
  () => props.proxyMode && (selection.uuids.value.length || selection.isDragging.value || selection.isMultiSelecting.value)
);
const fieldConfig = computed(() => {
  const match = types.getFieldConfig(
    props.entity.type,
    props.entity.bundle,
    props.name
  );
  if (!match) {
    throw new Error(
      `Missing field configuration for field "${props.name}" on entity type "${props.entity.type}" with bundle "${props.entity.bundle}". Make sure the "name" prop passed to <BlokkliField> is correct.`
    );
  }
  return match;
});
const allowedBundles = computed(() => {
  const bundles = fieldConfig.value.allowedBundles;
  if (!bundles.length) {
    console.error(
      `Field with name "${props.name}" on entity "${props.entity.type}" with bundle "${props.entity.bundle}" does not define any allowed bundles.`
    );
  }
  return bundles.join(",");
});
const fieldAttributes = computed(() => {
  return {
    "data-field-name": props.name,
    "data-field-label": fieldConfig.value.label,
    "data-field-is-nested": props.isNested,
    "data-bk-nesting-level": props.nestingLevel,
    "data-host-entity-type": props.entity.type,
    "data-host-entity-uuid": props.entity.uuid,
    "data-host-entity-bundle": props.entity.bundle,
    "data-field-key": props.fieldKey,
    "data-field-drop-alignment": props.dropAlignment,
    "data-allowed-fragments": props.allowedFragments ? props.allowedFragments.join(",") : void 0,
    "data-field-allowed-bundles": allowedBundles.value,
    "data-field-list-type": props.fieldListType,
    "data-field-cardinality": fieldConfig.value.cardinality
  };
});
function isMuted(item) {
  if (!item) {
    return true;
  }
  const isVisible = isVisibleByOptions(item, props.language);
  const isVisibleCustom = props.shouldRenderItem ? props.shouldRenderItem(item) : true;
  return !(isVisible && isVisibleCustom);
}
watch(root, function(newRoot) {
  if (newRoot) {
    dom.updateFieldElement(props.entity, props.name, newRoot);
  }
});
onMounted(() => {
  if (root.value) {
    dom.registerField(props.entity, props.name, root.value);
  }
});
onBeforeUnmount(() => {
  dom.unregisterField(props.entity, props.name);
});
defineOptions({
  inheritAttrs: false
});
if (import.meta.hot) {
  import.meta.hot.accept("#blokkli/helpers/runtimeHelpers", () => {
  });
}
</script>
